import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Twitter, Linkedin, Instagram, Youtube } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import useSiteSettings from "@/hooks/useSiteSettings";

const SOCIAL_ICONS = { twitter: Twitter, linkedin: Linkedin, instagram: Instagram, youtube: Youtube };

const Footer = () => {
  const settings = useSiteSettings();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.newsletter(email);
      toast.success("Subscribed. Welcome to World Visionary Times.");
      setEmail("");
    } catch { toast.error("Unable to subscribe. Please try again."); }
    finally { setSubmitting(false); }
  };

  const footer = settings?.footer || {};
  const description = footer.description || "The global voice of business, leadership and innovation.";
  const copyright = footer.copyright || "World Visionary Times. All rights reserved.";
  const social = footer.social_links || {};

  return (
    <footer data-testid="site-footer" className="relative z-[3] mt-16 border-t border-ink-line bg-cream">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Logo size="md" data-testid="footer-logo" />
            <p className="mt-6 text-ink-soft text-sm leading-relaxed max-w-sm">{description}</p>
            <div className="mt-6 flex items-center gap-3">
              {Object.entries(SOCIAL_ICONS).map(([key, Icon]) => (
                <a key={key} href={social[key] || "#"} target={social[key] && social[key] !== "#" ? "_blank" : undefined} rel="noreferrer" data-testid={`footer-social-${key}`} aria-label={key} className="w-9 h-9 flex items-center justify-center border border-ink-line rounded-full text-ink-soft hover:text-ink hover:border-ink transition-colors">
                  <Icon size={15} strokeWidth={1.4} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="overline mb-5">Publication</div>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/about", label: "About Us" },
                { to: "/magazine", label: "Magazine" },
                { to: "/articles", label: "Articles" },
                { to: "/interviews", label: "Interviews" },
                { to: "/news", label: "News" },
              ].map((l) => (
                <li key={l.to}>
                  <Link data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g,"-")}`} to={l.to} className="text-ink-soft hover:text-ink transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline mb-5">Categories</div>
            <ul className="space-y-3 text-sm">
              {["Business", "Technology", "Artificial Intelligence", "Leadership", "Finance", "Startups"].map((c) => (
                <li key={c}>
                  <Link data-testid={`footer-category-${c.toLowerCase().replace(/\s+/g,"-")}`} to={`/articles?category=${encodeURIComponent(c)}`} className="text-ink-soft hover:text-ink transition-colors">{c}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="overline mb-5">The Editor{"\u2019"}s Note</div>
            <p className="text-ink-soft text-sm mb-5">A weekly dispatch of our most considered reporting, delivered to your inbox on Saturday.</p>
            <form onSubmit={submit} data-testid="footer-newsletter-form" className="flex items-center gap-2 border-b border-ink pb-3">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" data-testid="footer-newsletter-input" className="flex-1 bg-transparent outline-none text-sm text-ink placeholder:text-ink-faint" />
              <button type="submit" disabled={submitting} data-testid="footer-newsletter-submit" className="text-ink text-sm font-medium tracking-wide hover:text-gold inline-flex items-center gap-1 disabled:opacity-50">
                Subscribe <ArrowUpRight size={14} />
              </button>
            </form>
            <p className="text-ink-muted text-[11px] mt-3">By subscribing you agree to our editorial and privacy terms.</p>
          </div>
        </div>

        <div className="hairline mt-14" />

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-ink-muted">
          <div>© {new Date().getFullYear()} {copyright}</div>
          <div className="flex items-center gap-6">
            <Link data-testid="footer-privacy" to="#" className="hover:text-ink transition-colors">Privacy Policy</Link>
            <Link data-testid="footer-terms" to="#" className="hover:text-ink transition-colors">Terms</Link>
            <Link data-testid="footer-editorial" to="/about" className="hover:text-ink transition-colors">Editorial Standards</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
